import * as React from 'react';
import classnames from 'classnames';
import { isBoolean } from 'lodash';

import ActionButtonDropdownControl from './control/ActionButtonDropdownControl';
import ActionButtonDropdownMenuContainer from './menu/container/ActionButtonDropdownMenuContainer';

import { expandDataProps } from 'utils/helper/common';
import { deselectAllItems, getUpdatedItem, updatePropValueByIdentifier } from './utils/action-button-dropdown';

import './action-button-dropdown.scss';

import type {
  ActionButtonDropdownColor,
  ActionButtonDropdownCustomSize,
  ActionButtonDropdownHeight,
  ActionButtonDropdownItem,
  ActionButtonDropdownStyle,
  ActionButtonDropdownWidth,
  Data,
  TextEntry
} from 'types';

type Props = {
  name: string,
  label?: string | React.ReactNode,
  items: Array<ActionButtonDropdownItem>,
  withSelectable?: boolean,
  withCustomWidthMenu?: boolean,
  withCenteredMenu?: boolean,
  withRightAlignedMenu?: boolean,
  withSpinner?: boolean,
  color?: ActionButtonDropdownColor,
  width?: ActionButtonDropdownWidth,
  height?: ActionButtonDropdownHeight,
  style?: ActionButtonDropdownStyle,
  disabled?: boolean,
  blocked?: boolean,
  className?: string,
  menuClassName?: string,
  menuItemClassName?: string,
  hideChevron?: boolean,
  data?: Data,
  customSize?: ActionButtonDropdownCustomSize,
  customButton?: (isOpen: boolean) => React.ReactNode,
  CustomMenu?: React.ReactNode,
  onClick?: (items: Array<ActionButtonDropdownItem>, data?: Data | null) => void
};

type State = {
  isMenuOpen: boolean,
  selectedItem: ActionButtonDropdownItem | undefined | null
};

class ActionButtonDropdown extends React.PureComponent<Props, State> {
  static defaultProps = {
    color: 'secondary',
    width: 'normal',
    height: 'normal',
    style: 'normal'
  };

  state = {
    isMenuOpen: false,
    selectedItem: this.props.items.find(item => item.isSelected)
  };

  controlRef = React.createRef<HTMLDivElement>();
  inputFieldRef: TextEntry | undefined | null = null;

  handleMenuToggle = (override?: boolean): void => {
    this.setState(state => ({
      isMenuOpen: isBoolean(override) ? override : !state.isMenuOpen
    }));
  };

  handleInputRefCallback = (ref: TextEntry): void => {
    this.inputFieldRef = ref;
  };

  handleInputBlur = (): void => {
    this.handleMenuToggle(false);
  };

  handleInputFocus = (): void => {
    if (this.inputFieldRef) {
      this.inputFieldRef.focus();
    }
  };

  handleItemClick = (identifier: string): void => {
    const { items, data, onClick } = this.props;

    this.handleMenuToggle(false);

    const updatedItem = getUpdatedItem(items, identifier);

    let updatedItems = deselectAllItems(items);
    updatedItems = updatePropValueByIdentifier(updatedItems, updatedItem?.identifier, 'isSelected');

    this.setState({
      selectedItem: updatedItem
    });

    const updatedDataValues = {
      ...data,
      identifier
    };

    if (onClick) {
      onClick(updatedItems, updatedDataValues);
    }
  };

  renderControl(): React.ReactNode {
    const {
      label,
      color,
      width,
      height,
      style,
      disabled,
      blocked,
      hideChevron,
      customSize,
      customButton,
      withSelectable,
      withSpinner
    } = this.props;

    const { isMenuOpen, selectedItem } = this.state;

    return (
      <div
        ref={this.controlRef}
        className="action-button-dropdown__control"
      >
        <ActionButtonDropdownControl
          selectedItem={selectedItem}
          withSelectable={withSelectable}
          withSpinner={withSpinner}
          label={label}
          color={color}
          width={width}
          height={height}
          style={style}
          isMenuOpen={isMenuOpen}
          disabled={disabled}
          blocked={blocked}
          hideChevron={hideChevron}
          customSize={customSize}
          CustomButton={customButton && customButton(isMenuOpen)}
          onInputRefCallback={this.handleInputRefCallback}
          onInputFocus={this.handleInputFocus}
          onInputBlur={this.handleInputBlur}
          onMenuToggle={this.handleMenuToggle}
        />
      </div>
    );
  }

  renderMenuContainer(): React.ReactNode {
    const {
      name,
      items,
      style,
      withSelectable,
      withCenteredMenu,
      withRightAlignedMenu,
      withCustomWidthMenu,
      menuClassName,
      menuItemClassName,
      customButton,
      CustomMenu
    } = this.props;

    const { isMenuOpen } = this.state;

    return (
      <ActionButtonDropdownMenuContainer
        key={`action-button-dropdown__menu-container-${name}`}
        controlRef={this.controlRef}
        items={items}
        withSelectable={withSelectable}
        withCenteredMenu={withCenteredMenu}
        withRightAlignedMenu={withRightAlignedMenu}
        withCustomWidthMenu={withCustomWidthMenu}
        CustomMenu={CustomMenu}
        isMenuOpen={isMenuOpen}
        hasCustomButton={Boolean(customButton && customButton(isMenuOpen))}
        style={style}
        menuClassName={menuClassName}
        menuItemClassName={menuItemClassName}
        onItemClick={this.handleItemClick}
      />
    );
  }

  render(): React.ReactNode {
    const { className, data } = this.props;
    const containerClasses = classnames('action-button-dropdown', className);

    return (
      <div
        className={containerClasses}
        {...expandDataProps(data)}
      >
        {this.renderControl()}
        {this.renderMenuContainer()}
      </div>
    );
  }
}

export default ActionButtonDropdown;
